import * as React from 'react'

import { Box, Button, Circle, Tag, Text } from '@factorialco/deprecated-design-system'
import { AnimatePresence } from 'framer-motion'
import {
  PerformanceReviewerStrategiesStrategyEnum,
  PerformanceStrategyConfigurationInput,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { WeightInput } from 'modules/performance_process/components/score_configuration_weights/ScoreWeightsSidepanel/WeightInput'
import { WeightTag } from 'modules/performance_process/components/score_configuration_weights/WeightTag'
import { type MappedStrategyQuestionnaire } from 'modules/performance_process/hooks/useMappedQuestionnaires'
import { useSectionWeights } from 'modules/performance_process/hooks/useStrategiesScoreConfiguration'
import { strategyColor, strategyTranslation } from 'modules/performance_process/lib/reviewStrategy'

type Props = {
  configuration: PerformanceStrategyConfigurationInput
  questionnaire: MappedStrategyQuestionnaire
  focusStrategy?: PerformanceReviewerStrategiesStrategyEnum | null
  onChange: (strategy: PerformanceReviewerStrategiesStrategyEnum, weight: number) => void
}

export const StrategyConfigurationItem: React.FC<Props> = ({
  configuration,
  questionnaire,
  focusStrategy,
  onChange,
}) => {
  const [collapsed, setCollapsed] = React.useState(true)
  const { sections, sectionWeights, isSectionWeightable } = useSectionWeights({
    questionnaire: questionnaire,
    weights: configuration.sectionWeights || [],
  })
  const strategy = configuration.reviewerStrategy
  const inputRef = React.useRef<HTMLInputElement | null>(null)

  React.useEffect(() => {
    if (focusStrategy === strategy && inputRef.current) {
      inputRef.current.focus()
    }
  }, [focusStrategy, strategy])

  const handleChange = (value: number | null) => {
    const weight = value ?? 0
    onChange(strategy, weight)
  }

  return (
    <Box
      flexDirection="column"
      gap="s16"
      paddingX="s24"
      paddingY="s16"
      borderRadius={{ all: 'abs016' }}
      border={{
        all: {
          style: 'solid',
          width: 's1',
          color: 'grey400',
        },
      }}
    >
      <Box flexDirection="row" alignItems="center">
        <Box flexDirection="row" gap="s4" alignItems="center" flexGrow>
          <Box alignSelf="center">
            <Circle color={strategyColor(strategy)[1]} size={16} />
          </Box>
          <Text.Normal size="300" weight="semibold" color="grey1000">
            {strategyTranslation(strategy)}
          </Text.Normal>
        </Box>
        <Box flexDirection="row" gap="s12" alignItems="center">
          {!configuration.producesScore && (
            <Tag>{i18n.t('performance_process.score_strategy_weights.no_score')}</Tag>
          )}
          <WeightInput
            value={configuration.weight}
            onChange={handleChange}
            weightable
            getRef={(ref) => (ref ? (inputRef.current = ref) : null)}
          />
          <Button
            onClick={() => setCollapsed(!collapsed)}
            type="secondary"
            iconName={collapsed ? 'chevron-down' : 'chevron-up'}
          />
        </Box>
      </Box>
      <AnimatePresence>
        {!collapsed && (
          <Box.Motion
            gap="s12"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ opacity: { duration: 0.1 }, height: { duration: 0.3 } }}
          >
            {sections.map((section) => (
              <Box
                paddingX="s12"
                paddingY="s20"
                key={section.uuid}
                flexDirection="row"
                background="grey100"
                borderRadius={{ all: 'abs016' }}
              >
                <Box paddingY="s6" flexDirection="row" flexGrow>
                  <Box flexGrow>
                    <Text.Normal size="300" weight="semibold" color="grey1000">
                      {section.title}
                    </Text.Normal>
                  </Box>
                  <WeightTag
                    weight={sectionWeights[section.uuid] || 0}
                    weightable={isSectionWeightable(section.uuid)}
                  />
                </Box>
              </Box>
            ))}
          </Box.Motion>
        )}
      </AnimatePresence>
    </Box>
  )
}
